import { TFunction } from 'i18next';
import {
  StudentMetricsDashboardProgressAtCurrentLevel,
  StudentMetricsDashboardLevelTransitions,
  StudentMetricsDashboardAcrossSkills,
  StudentMetricsDashboardFilters,
  StudentSkillPerformanceSortByQueryParam,
  StudentSkillPerformance,
  StudentSkillPerformanceQueryParams,
} from '@/school-management/types/generate-report';
import {
  StudentReportItem,
  StudentsReportQueryParams,
  StudentReportTeacher,
} from '@/school-management/types/response';
import { StudentsReportSortByQueryParams } from '@/school-management/types/response/student';
import { reportService } from '@/school-management/services/report';
import { studentService } from '@/school-management/services/student';
import { TableColumn } from '@/components/school-management/GenericTable/GenericTable';
import commonUtils from '@/school-management/utils/common';
import userUtils from '@/utils/user';
import styles from './StudentMetricsDashboard.module.scss';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { formatDateShort, formatMinutes } from '@/utils/formatters';
import { UserStatusEnum } from '@/school-management/types/response/studentDetails';
import classnames from 'classnames';
import React from 'react';
import { UserState } from '@/types/user';
import { formatPersonName, trimNamePart } from '@/utils/personName';
import ResetStudentPasswordLink from '@/components/school-management/ResetStudentPassword/ResetStudentPasswordLink';

export const skillsList = [
  'Listening',
  'Grammar',
  'Speaking',
  'Spelling',
  'Vocabulary',
  'Pronunciation',
  'Reading',
];
export const getSkillName = (skillId: number) =>
  skillsList.length >= skillId ? skillsList[skillId - 1] : skillId;
export const skillsForReport = [2, 3, 1, 5, 7, 4];
export const levelColors = ['orange', 'red', 'green', 'purple', 'blue'];
export const levelIds = [1, 2, 3, 4, 5];
export const MAX_SCHOOL_GRADE = 12;

export type StudentSkillPerformanceTable = StudentSkillPerformance & {
  name: string;
  skillScore1?: number;
  skillIsPass1?: boolean;
  skillScore2?: number;
  skillIsPass2?: boolean;
  skillScore3?: number;
  skillIsPass3?: boolean;
  skillScore4?: number;
  skillIsPass4?: boolean;
  skillScore5?: number;
  skillIsPass5?: boolean;
  skillScore6?: number;
  skillIsPass6?: boolean;
  skillScore7?: number;
  skillIsPass7?: boolean;
};

export const normalizeStudentMetricsDashboardProgressAtCurrentLevelData = (
  data?: StudentMetricsDashboardProgressAtCurrentLevel[]
) => {
  const levelsData = (data || []).reduce(
    (acc, item) => {
      if (item.level < 1 || item.level > 5) {
        return acc;
      }

      acc[item.level - 1].studentCount = item.studentCount;
      acc[item.level - 1].averageProgressPercent = item.averageProgressPercent;

      return acc;
    },
    levelIds.map(level => ({
      level: level,
      studentCount: 0,
      averageProgressPercent: 0,
      color: levelColors[level - 1],
    }))
  );

  return levelsData;
};

export const normalizeStudentMetricsDashboardLevelTransitionsData = (
  data?: StudentMetricsDashboardLevelTransitions[]
) => {
  const levelsData = (data || []).reduce(
    (acc, item) => {
      if (
        item.currentLevel < 1 ||
        item.currentLevel > 5 ||
        item.startingLevel < 1 ||
        item.startingLevel > 5
      ) {
        return acc;
      }

      acc[item.currentLevel - 1].studentCount += item.studentCount;
      acc[item.currentLevel - 1].parts[item.startingLevel - 1].studentCount = item.studentCount;
      acc[item.currentLevel - 1].parts[item.startingLevel - 1].percentage = item.percentage;

      return acc;
    },
    levelIds.map(level => ({
      level: level,
      studentCount: 0,
      parts: levelIds.map(part => ({
        startingLevel: part,
        studentCount: 0,
        percentage: 0,
        color: levelColors[part - 1],
      })),
    }))
  );

  levelsData.forEach(level => {
    level.parts = level.parts.filter(part => part.studentCount > 0);
  });

  return levelsData;
};

export const getAvgScoresDrillDownColumnsData = (t: TFunction) => {
  const columns: TableColumn<StudentSkillPerformanceTable>[] = [
    {
      key: 'name',
      label: t('studentMetricsDashboard.drillDown.skills.columns.nameAndTeacher'),
      width: '2fr',
      sortable: true,
      render: (value, row) => (
        <div className={styles.drillDownStudentName}>
          <span>{`${row.firstName} ${row.lastName}`}</span>
          {row.teachers && row.teachers.length > 0 && (
            <div className={styles.drillDownStudentTeachers}>
              <AppIcon icon='districts' />
              <div className={styles.drillDownStudentTeachersNames}>
                {(row.teachers || []).map((teacherName, index, arr) => (
                  <React.Fragment key={`${teacherName}-${index}`}>
                    <span className={styles.drillDownStudentTeacherName}>
                      {teacherName}
                      {index < arr.length - 1 ? ',' : ''}
                    </span>
                    {index < arr.length - 1 ? ' ' : ''}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'name1',
      label: t('studentMetricsDashboard.drillDown.skills.columns.name'),
      hidden: true,
      xlsxRender: (value, row) => formatPersonName(row.firstName, row.lastName),
    },
    {
      key: 'teacherNames',
      label: t('studentMetricsDashboard.drillDown.skills.columns.teacherNames'),
      hidden: true,
      xlsxRender: (value, row) => (row.teachers || []).join(', '),
    },
    {
      key: 'classNameAndGrade',
      label: t('studentMetricsDashboard.drillDown.skills.columns.classNameAndGrade'),
      width: '1fr',
      sortable: true,
      render: (value, row) => (
        <div>
          <div>{row.className}</div>
          <div>
            {row.gradeId
              ? `${commonUtils.getGradeName(Number(row.gradeId), t)}${
                  Number(row.gradeId) <= MAX_SCHOOL_GRADE
                    ? ` ${t('studentMetricsDashboard.drillDown.skills.columns.grade')}`
                    : ''
                }`
              : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'className',
      label: t('studentMetricsDashboard.drillDown.skills.columns.class'),
      hidden: true,
      xlsxRender: (value, row) => value,
    },
    {
      key: 'gradeId',
      label: t('studentMetricsDashboard.drillDown.skills.columns.grade'),
      hidden: true,
      xlsxRender: (value, row) =>
        row.gradeId ? commonUtils.getGradeName(Number(row.gradeId), t) : '',
    },
    {
      key: 'currentLevel',
      label: t('studentMetricsDashboard.drillDown.skills.columns.currentLevel'),
      width: '1fr',
      sortable: true,
      render: (value, row) => (row.currentLevel <= 0 ? '-' : row.currentLevel),
      xlsxRender: (value, row) => (row.currentLevel <= 0 ? '-' : row.currentLevel),
    },
    ...skillsForReport.map(skillId => {
      return {
        key: `skillScore${skillId}`,
        label: t(`studentMetricsDashboard.drillDown.skills.columns.skill${getSkillName(skillId)}`),
        width: '1fr',
        sortable: true,
        render: (value, row) =>
          typeof value === 'undefined' ? (
            '-'
          ) : (row as any)[`skillIsPass${skillId}`] ? (
            value
          ) : (
            <span style={{ color: '#F33E3E' }}>{value}</span>
          ),
        xlsxRender: (value, row) => (typeof value === 'undefined' ? '-' : value),
      } as TableColumn<StudentSkillPerformanceTable>;
    }),
    {
      key: 'actions',
      label: '',
      width: '0px',
      render: () => '',
    },
  ];

  return columns;
};

export const getAvgScoresDrillDownData = async (
  filters: StudentMetricsDashboardFilters,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: StudentSkillPerformanceSortByQueryParam;
    sortDirection?: 'asc' | 'desc';
    sortBySkillId?: number;
  }
) => {
  const data: StudentSkillPerformanceTable[] = [];
  const paginationData = {
    hasNextPage: false,
    hasPreviousPage: false,
    page: 1,
    pageSize: 0,
    totalCount: 0,
    totalPages: 1,
  };

  try {
    const getOnePage = !!options?.page;
    let pageNamber = options?.page ? options.page - 1 : 0;
    let hasNextPage = true;

    const queryParams: StudentSkillPerformanceQueryParams = {
      ...filters,
      page: pageNamber,
      pageSize: options?.pageSize || 1000,
      sortBy: options?.sortBy,
      sortDirection: options?.sortDirection,
      sortBySkillId: options?.sortBySkillId,
    };

    while (hasNextPage) {
      pageNamber++;

      queryParams.page = pageNamber;
      const response = (await reportService.fetchStudentSkillPerformance(queryParams))?.data?.data;

      if (response?.students && response.students.length > 0) {
        data.push(
          ...response.students.map(s => {
            const item: any = {
              ...s,
              name: `${s.firstName} ${s.firstName}`,
            };

            if (item.skillScores) {
              skillsForReport.forEach(skillId => {
                if (item.skillScores[skillId]) {
                  item[`skillScore${skillId}`] = item.skillScores[skillId].score;
                  item[`skillIsPass${skillId}`] = item.skillScores[skillId].status === 'Pass';
                }
              });
            }

            return item as StudentSkillPerformanceTable;
          })
        );
      }

      if (response) {
        paginationData.hasNextPage = response.hasNextPage;
        paginationData.hasPreviousPage = response.hasPreviousPage;
        paginationData.page = response.page;
        paginationData.pageSize = response.pageSize;
        paginationData.totalCount = response.totalCount;
        paginationData.totalPages = response.totalPages;
      }

      hasNextPage = getOnePage ? false : !!paginationData.hasNextPage;
    }
  } catch (er) {}

  return {
    data,
    paginationData,
  };
};

export type StudentsTableColumnsVariant = 'default' | 'classView';

const formatStudentScoreCell = (row: StudentReportItem): string => {
  const r = row as StudentReportItem & {
    score?: number | null;
    average_score?: number | null;
  };
  const raw = r.averageScore ?? r.average_score ?? r.score ?? null;
  if (raw == null || Number.isNaN(Number(raw))) {
    return '—';
  }
  return String(Number(raw));
};

export const getStudentsColumnsData = (
  t: TFunction,
  user: UserState | null,
  variant: StudentsTableColumnsVariant = 'default'
) => {
  const isTeacher = userUtils.isTeacher(user);
  const userId = user?.id;
  const getStatusInfo = (userStatus?: UserStatusEnum, level?: number) => {
    if (
      (userStatus === UserStatusEnum.ACTIVE ||
        userStatus === UserStatusEnum.LEVEL_UP ||
        userStatus === UserStatusEnum.LEVEL_DOWN ||
        userStatus === UserStatusEnum.LEVEL_COMPLETED_BLOCKED) &&
      level
    ) {
      return { text: t('active'), colorClass: styles.statusDotGreen };
    }
    if (userStatus === UserStatusEnum.INACTIVE) {
      return { text: t('inactive'), colorClass: styles.statusDotGray };
    }
    if (userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE) {
      return { text: t('levelTestIncomplete'), colorClass: styles.statusDotBlue };
    }
    if (userStatus === UserStatusEnum.RESET_PASSWORD) {
      return { text: t('resetPassword'), colorClass: styles.statusDotRed };
    }
    return null;
  };

  const getStatusRow = (row: StudentReportItem) => {
    const { userStatus, currentLevel } = row;
    const statusInfo = getStatusInfo(userStatus, currentLevel);
    if (!statusInfo) return null;

    return (
      <div className={styles.drillDownStudentStatus}>
        <div className={classnames(styles.statusDot, statusInfo.colorClass)} />
        <span
          className={classnames(styles.statusText, {
            [styles.statusTextInactive]: userStatus === UserStatusEnum.INACTIVE,
            [styles.statusTextLevelTest]: userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE,
            [styles.statusTextReset]: userStatus === UserStatusEnum.RESET_PASSWORD,
          })}
        >
          {userStatus === UserStatusEnum.RESET_PASSWORD ? (
            <ResetStudentPasswordLink
              studentId={row.userId}
              studentUsername={row.username}
              studentName={row.name}
            >
              {statusInfo.text}
            </ResetStudentPasswordLink>
          ) : (
            statusInfo.text
          )}
        </span>
      </div>
    );
  };

  const getTeachersList = (teachers: StudentReportTeacher[]) => {
    const teachersList = teachers || [];

    if (!isTeacher) {
      return teachersList;
    }

    return teachersList.filter(t => t.userId !== userId);
  };

  const columns: TableColumn<StudentReportItem>[] = [
    {
      key: 'name',
      label:
        variant === 'classView'
          ? t('schoolDetailsPage.classes.viewClassPage.studentColumnName')
          : isTeacher
            ? t('studentMetricsDashboard.drillDown.students.columns.nameAndCoTeacher')
            : t('studentMetricsDashboard.drillDown.students.columns.nameAndTeacher'),
      width: '2fr',
      sortable: true,
      render: (value, row) => (
        <div className={styles.drillDownStudentName}>
          <span>{row.name}</span>
          {getStatusRow(row)}
          {variant !== 'classView' && row.teachers && row.teachers.length > 0 && (
            <div className={styles.drillDownStudentTeachers}>
              <AppIcon icon='districts' />
              <div>
                {getTeachersList(row.teachers).map((teacher, index, arr) => (
                  <React.Fragment key={`${teacher.userId}-${index}`}>
                    <span className={styles.drillDownStudentTeacherName}>
                      {`${teacher.firstName} ${teacher.lastName}`}
                      {index < arr.length - 1 ? ',' : ''}
                    </span>
                    {index < arr.length - 1 ? ' ' : ''}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'name1',
      label: t('studentMetricsDashboard.drillDown.students.columns.name'),
      hidden: true,
      xlsxRender: (value, row) => trimNamePart(row.name),
    },
    {
      key: 'teacherNames',
      label: t(
        isTeacher
          ? 'studentMetricsDashboard.drillDown.students.columns.coTeacherName'
          : 'studentMetricsDashboard.drillDown.students.columns.teacherNames'
      ),
      hidden: true,
      xlsxRender: (value, row) =>
        getTeachersList(row.teachers)
          .map(t => formatPersonName(t.firstName, t.lastName))
          .join(', '),
    },
    {
      key: 'groupName',
      label: t('studentMetricsDashboard.drillDown.students.columns.classNameAndGrade'),
      width: '1.5fr',
      sortable: true,
      render: (value, row) => (
        <div>
          <div>{row.groupName}</div>
          <div>
            {row.gradeId
              ? `${commonUtils.getGradeName(Number(row.gradeId), t)}${
                  Number(row.gradeId) <= MAX_SCHOOL_GRADE
                    ? ` ${t('studentMetricsDashboard.drillDown.students.columns.grade')}`
                    : ''
                }`
              : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'groupName1',
      label: t('studentMetricsDashboard.drillDown.students.columns.class'),
      hidden: true,
      xlsxRender: (value, row) => row.groupName || '',
    },
    {
      key: 'gradeId',
      label: t('studentMetricsDashboard.drillDown.students.columns.grade'),
      hidden: true,
      xlsxRender: (value, row) => (row.gradeId ? commonUtils.getGradeName(row.gradeId, t) : ''),
    },
    {
      key: 'startingLevel',
      label: t('studentMetricsDashboard.drillDown.students.columns.startingLevel'),
      width: '1fr',
      sortable: true,
      render: (value, row) =>
        row.startingLevel <= 0
          ? '-'
          : row.startingLevel + (row.isStartingLevelChangedByTeacher ? '*' : ''),
      xlsxRender: (value, row) => (row.startingLevel <= 0 ? '-' : row.startingLevel),
    },
    {
      key: 'currentLevel',
      label: t('studentMetricsDashboard.drillDown.students.columns.currentLevel'),
      width: '1fr',
      sortable: true,
      render: (value, row) => (row.currentLevel <= 0 ? '-' : row.currentLevel),
      xlsxRender: (value, row) => (row.currentLevel <= 0 ? '-' : row.currentLevel),
    },
    {
      key: 'completedActivities',
      label: t('studentMetricsDashboard.drillDown.students.columns.progress'),
      width: '1fr',
      sortable: true,
      render: (value, row) =>
        `${row.completedActivities < 0 ? 0 : row.completedActivities} (${row.progress < 0 ? 0 : row.progress}%)`,
      xlsxRender: (value, row) =>
        `${row.completedActivities < 0 ? 0 : row.completedActivities} (${row.progress < 0 ? 0 : row.progress}%)`,
    },
    {
      key: 'timeSpentSeconds',
      label: t('studentMetricsDashboard.drillDown.students.columns.timeSpent'),
      width: '1fr',
      sortable: true,
      render: (value, row) => formatMinutes(row.timeSpentSeconds),
      xlsxRender: (value, row) => formatMinutes(row.timeSpentSeconds),
    },
    {
      key: 'score',
      label: t('studentMetricsDashboard.drillDown.students.columns.score'),
      width: '1fr',
      sortable: true,
      render: (value, row) =>
        typeof row.score === 'number' && !Number.isNaN(row.score) ? row.score : '-',
      xlsxRender: (value, row) =>
        typeof row.score === 'number' && !Number.isNaN(row.score) ? row.score : '-',
    },
    // firstLogin, lastLogin, actions: appended below for default variant only (avoids duplicates with classView).
  ];

  const firstLoginCol: TableColumn<StudentReportItem> = {
    key: 'firstLogin',
    label: t('studentMetricsDashboard.drillDown.students.columns.firstLogin'),
    width: '1fr',
    sortable: true,
    render: (value, row) => {
      let firstLoginAtValue = '';
      if (
        (!row.firstLogin || row.firstLogin === 'N/A' || row.firstLogin === '') &&
        row.lastLogin &&
        row.lastLogin !== ''
      ) {
        firstLoginAtValue = t('studentMetricsDashboard.drillDown.students.notAvailable');
      } else if (row.firstLogin && row.firstLogin !== 'N/A') {
        firstLoginAtValue = formatDateShort(
          row.firstLogin.toUpperCase().endsWith('Z') ? row.firstLogin : `${row.firstLogin}Z`
        );
      }

      return firstLoginAtValue;
    },
    xlsxRender: (value, row) => {
      let firstLoginAtValue = '';
      if (
        (!row.firstLogin || row.firstLogin === 'N/A' || row.firstLogin === '') &&
        row.lastLogin &&
        row.lastLogin !== ''
      ) {
        firstLoginAtValue = t('studentMetricsDashboard.drillDown.students.notAvailable');
      } else if (row.firstLogin && row.firstLogin !== 'N/A') {
        firstLoginAtValue = formatDateShort(
          row.firstLogin.toUpperCase().endsWith('Z') ? row.firstLogin : `${row.firstLogin}Z`
        );
      }

      return firstLoginAtValue;
    },
  };

  const lastLoginCol: TableColumn<StudentReportItem> = {
    key: 'lastLogin',
    label: t('studentMetricsDashboard.drillDown.students.columns.lastLogin'),
    width: '1fr',
    sortable: true,
    render: (value, row) => {
      let lastLogin = row.lastLogin || '';

      if (lastLogin && !lastLogin.toUpperCase().endsWith('Z')) {
        lastLogin = `${lastLogin}Z`;
      }

      if (lastLogin) {
        lastLogin = formatDateShort(lastLogin);
      }

      return lastLogin;
    },
    xlsxRender: (value, row) => {
      let lastLogin = row.lastLogin || '';

      if (lastLogin && !lastLogin.toUpperCase().endsWith('Z')) {
        lastLogin = `${lastLogin}Z`;
      }

      if (lastLogin) {
        lastLogin = formatDateShort(lastLogin);
      }

      return lastLogin;
    },
  };

  const scoreCol: TableColumn<StudentReportItem> = {
    key: 'averageScore',
    label: t('studentMetricsDashboard.drillDown.students.columns.score'),
    width: '1fr',
    sortable: true,
    render: (_value, row) => formatStudentScoreCell(row),
    xlsxRender: (_value, row) => {
      const s = formatStudentScoreCell(row);
      return s === '—' ? '' : s;
    },
  };

  const actionsCol: TableColumn<StudentReportItem> = {
    key: 'actions',
    label: '',
    width: '0px',
    render: () => '',
  };

  if (variant === 'classView') {
    const withoutClassAndGrade = columns.filter(
      c => c.key !== 'groupName' && c.key !== 'gradeId' && c.key !== 'score' // classView uses averageScore via scoreCol
    );
    return [...withoutClassAndGrade, scoreCol, actionsCol];
  }

  return [...columns, firstLoginCol, lastLoginCol, actionsCol];
};

export const getStudentsDrillDownData = async (
  filters: StudentMetricsDashboardFilters,
  schoolId: string,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: StudentsReportSortByQueryParams;
    sortDirection?: 'asc' | 'desc';
  }
) => {
  const data: StudentReportItem[] = [];
  const paginationData = {
    hasNextPage: false,
    hasPreviousPage: false,
    page: 1,
    pageSize: 0,
    totalCount: 0,
    totalPages: 1,
  };

  try {
    const getOnePage = !!options?.page;
    let pageNamber = options?.page ? options.page - 1 : 0;
    let hasNextPage = true;

    const queryParams: StudentsReportQueryParams = {
      page: pageNamber,
      pageSize: options?.pageSize || 1000,
      sortBy: options?.sortBy,
      sortDirection: options?.sortDirection,
      hasActivities: true,
    };

    if (filters.teacherIds && filters.teacherIds.length > 0) {
      queryParams.teacherIds = filters.teacherIds;
    }
    if (filters.groupIds && filters.groupIds.length > 0) {
      queryParams.groupIds = filters.groupIds;
    }
    if (filters.levelIds && filters.levelIds.length > 0) {
      queryParams.levels = {
        currentList: filters.levelIds,
      };
    }
    if (filters.gradeIds && filters.gradeIds.length > 0) {
      queryParams.gradeIds = filters.gradeIds;
    }

    while (hasNextPage) {
      pageNamber++;

      queryParams.page = pageNamber;
      const response = await studentService.getAllStudents(schoolId, queryParams);

      if (response?.data?.items && response.data.items.length > 0) {
        data.push(...response.data.items);
      }

      if (response?.data) {
        paginationData.hasNextPage = response.data.hasNextPage;
        paginationData.hasPreviousPage = response.data.hasPreviousPage;
        paginationData.page = response.data.page;
        paginationData.pageSize = response.data.pageSize;
        paginationData.totalCount = response.data.totalCount;
        paginationData.totalPages = response.data.totalPages;
      }

      hasNextPage = getOnePage ? false : !!response?.data?.hasNextPage;
    }
  } catch (er) {}

  return {
    data,
    paginationData,
  };
};
